import { autoUpdate, flip, offset, Placement, shift, useFloating, useHover, useInteractions } from '@floating-ui/react';
import React, { useState } from 'react';
import type { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { Text } from 'widgets';

interface TooltipProps {
    tooltip?: JSX.Element | string;
    placement?: Placement;
    width?: string;
    wrapperClassName?: string;
}

export const Tooltip = ({
    children,
    tooltip,
    placement = 'top',
    width,
    wrapperClassName,
}: PropsWithChildren<TooltipProps>) => {
    const [open, setOpen] = useState(false);

    const { x, y, refs, strategy, context } = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([useHover(context)]);

    if (!tooltip) {
        return <>{children}</>;
    }

    return (
        <>
            <Wrapper className={wrapperClassName} {...getReferenceProps({ ref: refs.setReference })}>
                {children}
            </Wrapper>
            {open && (
                <Container
                    {...getFloatingProps({
                        ref: refs.setFloating,
                        style: {
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                            width,
                        },
                    })}
                >
                    <Text helper>{tooltip}</Text>
                </Container>
            )}
        </>
    );
};

const Wrapper = styled.div``;

const Container = styled.div`
    background-color: white;
    color: ${({ theme }) => theme.colors.cocoa1000};
    pointer-events: none;
    border: 1px solid ${({ theme }) => theme.colors.cocoa100};
    border-radius: 6px;
    box-shadow:
        0 4px 6px -1px rgb(0 0 0 / 0.1),
        0 2px 4px -2px rgb(0 0 0 / 0.1);
    padding: 4px 8px;
    z-index: 1;
`;
